// frontend/src/services/approver.service.ts - UPDATED WITH APPROVAL REMARKS
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  addDoc,
  or
} from 'firebase/firestore';
import { db } from './firebase';

export interface OvertimeRequest {
  id: string;
  projectName: string;
  projectCode: string;
  requesterId: string;
  requesterName: string;
  approverEmpId: string;
  approverName: string;
  status: 'pending' | 'approved' | 'rejected';
  startTime: Date;
  endTime: Date;
  totalHours: number;
  totalEmployees: number;
  employeeIds: string[];
  employeeDetails: EmployeeDetail[];
  requestTime: Date;
  remarks?: string;
  responseMessage?: string;
  responseTime?: Date;
  isActive: boolean;
  // Additional fields for tracking who actually processed
  actualApproverId?: string;
  actualApproverName?: string;
  approvedBy?: string;
  rejectedBy?: string;
  approvedAt?: Date;
  rejectedAt?: Date;
  // NEW: Approval remarks
  approvalRemarks?: string;
  rejectionReason?: string;
}

export interface EmployeeDetail {
  id: string;
  name: string;
  designation: string;
  department: string;
  employeeNumber: string;
  pin?: string;
}

export interface ApprovalAction {
  requestId: string;
  action: 'approve' | 'reject';
  reason?: string; // This will be used for both approval and rejection remarks
  approverId: string;
  approverName: string;
}

export interface RequestFilters {
  status?: 'all' | 'pending' | 'approved' | 'rejected';
  dateFrom?: Date;
  dateTo?: Date;
  projectName?: string;
}

class ApproverService {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 1 * 60 * 1000;

  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      console.log('Using cached data for:', key);
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  async getOvertimeRequests(approverId: string, filters?: RequestFilters): Promise<OvertimeRequest[]> {
    try {
      console.log('🔍 Fetching ALL overtime requests for approver:', approverId);

      const cacheKey = `requests_${approverId}_${JSON.stringify(filters || {})}`;
      const cached = this.getFromCache<OvertimeRequest[]>(cacheKey);
      if (cached) return cached;

      let allRequests: OvertimeRequest[] = [];

      try {
        console.log('📋 Fetching ALL pending requests...');
        
        const pendingQuery = query(
          collection(db, 'overtime_requests'),
          where('status', '==', 'pending'),
          orderBy('requestTime', 'desc'),
          limit(100)
        );

        const pendingSnapshot = await getDocs(pendingQuery);
        console.log(`Found ${pendingSnapshot.docs.length} pending requests`);

        for (const docSnap of pendingSnapshot.docs) {
          const request = this.convertDocToRequest(docSnap);
          if (this.matchesFilters(request, filters)) {
            allRequests.push(request);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching pending requests:', error);
      }

      try {
        console.log('📋 Fetching ALL approved requests...');
        
        const approvedQuery = query(
          collection(db, 'overtime_requests'),
          where('status', '==', 'approved'),
          orderBy('responseTime', 'desc'),
          limit(100)
        );

        const approvedSnapshot = await getDocs(approvedQuery);
        console.log(`Found ${approvedSnapshot.docs.length} approved requests`);

        for (const docSnap of approvedSnapshot.docs) {
          const exists = allRequests.some(r => r.id === docSnap.id);
          if (!exists) {
            const request = this.convertDocToRequest(docSnap);
            if (this.matchesFilters(request, filters)) {
              allRequests.push(request);
            }
          }
        }
      } catch (error) {
        console.error('❌ Error fetching approved requests:', error);
      }

      try {
        console.log('📋 Fetching ALL rejected requests...');
        
        const rejectedQuery = query(
          collection(db, 'overtime_requests'),
          where('status', '==', 'rejected'),
          orderBy('responseTime', 'desc'),
          limit(100)
        );

        const rejectedSnapshot = await getDocs(rejectedQuery);
        console.log(`Found ${rejectedSnapshot.docs.length} rejected requests`);

        for (const docSnap of rejectedSnapshot.docs) {
          const exists = allRequests.some(r => r.id === docSnap.id);
          if (!exists) {
            const request = this.convertDocToRequest(docSnap);
            if (this.matchesFilters(request, filters)) {
              allRequests.push(request);
            }
          }
        }
      } catch (error) {
        console.error('❌ Error fetching rejected requests:', error);
      }

      if (allRequests.length === 0) {
        try {
          console.log('📋 Trying to fetch ALL requests without status filter...');
          
          const allRequestsQuery = query(
            collection(db, 'overtime_requests'),
            orderBy('requestTime', 'desc'),
            limit(200)
          );

          const allSnapshot = await getDocs(allRequestsQuery);
          console.log(`Found ${allSnapshot.docs.length} total requests`);

          for (const docSnap of allSnapshot.docs) {
            const request = this.convertDocToRequest(docSnap);
            if (this.matchesFilters(request, filters)) {
              allRequests.push(request);
            }
          }
        } catch (error) {
          console.error('❌ Error fetching all requests:', error);
        }
      }

      allRequests.sort((a, b) => {
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (b.status === 'pending' && a.status !== 'pending') return 1;
        
        const aTime = a.status === 'pending' ? a.requestTime : (a.responseTime || a.requestTime);
        const bTime = b.status === 'pending' ? b.requestTime : (b.responseTime || b.requestTime);
        
        return bTime.getTime() - aTime.getTime();
      });

      this.setCache(cacheKey, allRequests);
      console.log(`✅ Total unique requests found: ${allRequests.length}`);
      console.log(`📊 Breakdown: ${allRequests.filter(r => r.status === 'pending').length} pending, ${allRequests.filter(r => r.status === 'approved').length} approved, ${allRequests.filter(r => r.status === 'rejected').length} rejected`);
      
      return allRequests;
    } catch (error) {
      console.error('❌ Error fetching overtime requests:', error);
      throw new Error(`Failed to fetch requests: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private convertDocToRequest(docSnap: any): OvertimeRequest {
    const data = docSnap.data();
    
    return {
      id: docSnap.id,
      projectName: data.projectName || '',
      projectCode: data.projectCode || '',
      requesterId: data.requesterId || '',
      requesterName: data.requesterName || '',
      approverEmpId: data.approverEmpId || '',
      approverName: data.approverName || '',
      status: data.status || 'pending',
      startTime: data.startTime?.toDate() || new Date(),
      endTime: data.endTime?.toDate() || new Date(),
      totalHours: data.totalHours || 0,
      totalEmployees: data.totalEmployees || 0,
      employeeIds: data.employeeIds || [],
      employeeDetails: data.employeeDetails || [],
      requestTime: data.requestTime?.toDate() || new Date(),
      remarks: data.remarks || '',
      responseMessage: data.responseMessage || data.approvalRemarks || data.rejectionReason || '',
      responseTime: data.responseTime?.toDate() || data.approvedAt?.toDate() || data.rejectedAt?.toDate(),
      isActive: data.isActive !== false,
      actualApproverId: data.actualApproverId || data.approverEmpId,
      actualApproverName: data.actualApproverName || data.approvedBy || data.rejectedBy,
      approvedBy: data.approvedBy,
      rejectedBy: data.rejectedBy,
      approvedAt: data.approvedAt?.toDate(),
      rejectedAt: data.rejectedAt?.toDate(),
      // NEW: Approval and rejection remarks
      approvalRemarks: data.approvalRemarks || '',
      rejectionReason: data.rejectionReason || ''
    };
  }

  async getPendingRequests(approverId: string, limitCount = 10): Promise<OvertimeRequest[]> {
    try {
      const allRequests = await this.getOvertimeRequests(approverId, { status: 'pending' });
      return allRequests.slice(0, limitCount);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      return [];
    }
  }

  async getRecentRequests(approverId: string): Promise<OvertimeRequest[]> {
    try {
      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);

      const allRequests = await this.getOvertimeRequests(approverId);
      return allRequests.filter(request => request.requestTime >= lastWeek);
    } catch (error) {
      console.error('Error fetching recent requests:', error);
      return [];
    }
  }

  /**
   * UPDATED: Approve or reject an overtime request with remarks
   */
  async processRequest(action: ApprovalAction): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🔄 Processing overtime request:', action);

      // Validation: Rejection requires a reason
      if (action.action === 'reject' && !action.reason?.trim()) {
        throw new Error('Rejection reason is required');
      }

      const requestRef = doc(db, 'overtime_requests', action.requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        throw new Error('Overtime request not found');
      }

      const requestData = requestDoc.data();
      if (requestData.status !== 'pending') {
        throw new Error('This request has already been processed');
      }

      // UPDATED: Prepare update data with remarks for both approve and reject
      const updateData: any = {
        status: action.action === 'approve' ? 'approved' : 'rejected',
        responseTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
        version: (requestData.version || 1) + 1,
        actualApproverId: action.approverId,
        actualApproverName: action.approverName
      };

      if (action.action === 'approve') {
        updateData.approvedBy = action.approverName;
        updateData.approvedAt = serverTimestamp();
        // NEW: Save approval remarks (optional)
        if (action.reason?.trim()) {
          updateData.approvalRemarks = action.reason.trim();
          updateData.responseMessage = action.reason.trim();
        } else {
          updateData.approvalRemarks = '';
          updateData.responseMessage = 'Approved';
        }
      } else {
        updateData.rejectedBy = action.approverName;
        updateData.rejectedAt = serverTimestamp();
        updateData.rejectionReason = action.reason;
        updateData.responseMessage = action.reason;
      }

      await updateDoc(requestRef, updateData);

      await this.sendProcessingNotifications({
        requestId: action.requestId,
        requestData,
        action: action.action,
        reason: action.reason,
        approverId: action.approverId,
        approverName: action.approverName
      });

      this.clearCache();

      const successMessage = action.action === 'approve' 
        ? 'Overtime request approved successfully'
        : 'Overtime request rejected successfully';

      console.log('✅', successMessage);
      return { success: true, message: successMessage };

    } catch (error) {
      console.error('❌ Error processing request:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to process request'
      };
    }
  }

  async getApproverStatistics(approverId: string): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    todayRequests: number;
    totalEmployees: number;
    totalHours: number;
  }> {
    try {
      console.log('📊 Calculating approver statistics...');
      
      const allRequests = await this.getOvertimeRequests(approverId);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const stats = {
        total: allRequests.length,
        pending: allRequests.filter(r => r.status === 'pending').length,
        approved: allRequests.filter(r => r.status === 'approved').length,
        rejected: allRequests.filter(r => r.status === 'rejected').length,
        todayRequests: allRequests.filter(r => r.requestTime >= today).length,
        totalEmployees: allRequests.reduce((sum, r) => sum + r.totalEmployees, 0),
        totalHours: allRequests.reduce((sum, r) => sum + r.totalHours, 0)
      };

      console.log('📊 Statistics calculated:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Error getting approver statistics:', error);
      return {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        todayRequests: 0,
        totalEmployees: 0,
        totalHours: 0
      };
    }
  }

  async getRequestEmployeeDetails(requestId: string): Promise<EmployeeDetail[]> {
    try {
      const requestDoc = await getDoc(doc(db, 'overtime_requests', requestId));
      if (!requestDoc.exists()) {
        throw new Error('Request not found');
      }

      const data = requestDoc.data();
      return data.employeeDetails || [];
    } catch (error) {
      console.error('Error getting employee details:', error);
      return [];
    }
  }

  private matchesFilters(request: OvertimeRequest, filters?: RequestFilters): boolean {
    if (!filters) return true;

    if (filters.status && filters.status !== 'all' && request.status !== filters.status) {
      return false;
    }

    if (filters.dateFrom && request.requestTime < filters.dateFrom) {
      return false;
    }

    if (filters.dateTo && request.requestTime > filters.dateTo) {
      return false;
    }

    if (filters.projectName && !request.projectName.toLowerCase().includes(filters.projectName.toLowerCase())) {
      return false;
    }

    return true;
  }

  private async sendProcessingNotifications(data: {
    requestId: string;
    requestData: any;
    action: 'approve' | 'reject';
    reason?: string;
    approverId: string;
    approverName: string;
  }): Promise<void> {
    try {
      console.log('📧 Sending processing notifications...');

      const notificationsRef = collection(db, 'notifications');

      const requesterNotification = {
        type: 'overtime_request_update',
        targetUserId: data.requestData.requesterId,
        title: data.action === 'approve' ? 'Overtime Request Approved!' : 'Overtime Request Rejected',
        body: data.action === 'approve' 
          ? `Your overtime request for ${data.requestData.projectName} has been approved by ${data.approverName}.${data.reason ? ` Remarks: ${data.reason}` : ''}`
          : `Your overtime request for ${data.requestData.projectName} has been rejected. Reason: ${data.reason}`,
        data: {
          requestId: data.requestId,
          projectName: data.requestData.projectName,
          status: data.action === 'approve' ? 'approved' : 'rejected',
          approverName: data.approverName,
          reason: data.reason || '',
          remarks: data.reason || ''
        },
        timestamp: serverTimestamp(),
        processed: false
      };

      await addDoc(notificationsRef, requesterNotification);

      if (data.action === 'approve' && data.requestData.employeeIds?.length > 0) {
        for (const employeeId of data.requestData.employeeIds) {
          const employeeNotification = {
            type: 'overtime_approved',
            targetUserId: employeeId,
            title: 'You\'re Approved for Overtime!',
            body: `You have been approved for overtime work in ${data.requestData.projectName}.${data.reason ? ` Remarks: ${data.reason}` : ''}`,
            data: {
              requestId: data.requestId,
              projectName: data.requestData.projectName,
              projectCode: data.requestData.projectCode,
              remarks: data.reason || ''
            },
            timestamp: serverTimestamp(),
            processed: false
          };

          try {
            await addDoc(notificationsRef, employeeNotification);
          } catch (error) {
            console.error(`❌ Error notifying employee ${employeeId}:`, error);
          }
        }
      }

      console.log('✅ Processing notifications sent successfully');
    } catch (error) {
      console.error('❌ Error sending processing notifications:', error);
    }
  }

  clearCache(): void {
    this.cache.clear();
    console.log('🗑️ Approver service cache cleared');
  }

  async testDataAvailability(): Promise<{ success: boolean; message: string; counts: any }> {
    try {
      console.log('🧪 Testing data availability...');
      
      const pendingQuery = query(collection(db, 'overtime_requests'), where('status', '==', 'pending'), limit(10));
      const approvedQuery = query(collection(db, 'overtime_requests'), where('status', '==', 'approved'), limit(10));
      const rejectedQuery = query(collection(db, 'overtime_requests'), where('status', '==', 'rejected'), limit(10));
      
      const [pendingSnap, approvedSnap, rejectedSnap] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(approvedQuery), 
        getDocs(rejectedQuery)
      ]);
      
      const counts = {
        pending: pendingSnap.docs.length,
        approved: approvedSnap.docs.length,
        rejected: rejectedSnap.docs.length,
        total: pendingSnap.docs.length + approvedSnap.docs.length + rejectedSnap.docs.length
      };
      
      console.log('🧪 Data availability test results:', counts);
      
      return {
        success: true,
        message: `Data test successful. Found ${counts.total} total requests.`,
        counts
      };
    } catch (error) {
      console.error('❌ Data availability test failed:', error);
      return {
        success: false,
        message: `Data test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        counts: { pending: 0, approved: 0, rejected: 0, total: 0 }
      };
    }
  }
}

export const approverService = new ApproverService();